'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Copy, Loader2, RotateCcw, Save, Send, XCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api/client'
import type {
  HelmStaffInvitation,
  HelmUser,
  HelmUserRole,
} from '@/lib/api/types'

const roles: Array<{ value: HelmUserRole; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'forwarder', label: 'Forwarder' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'customer', label: 'Customer' },
  { value: 'viewer', label: 'Viewer' },
]

function formatRole(role: HelmUserRole) {
  return roles.find((item) => item.value === role)?.label ?? role
}

function formatDate(value?: string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleDateString()
}

function getRoleValues(user: HelmUser) {
  return user.helm_roles.map((assignment) => assignment.role)
}

function RoleBadges({ roles: roleValues }: { roles: HelmUserRole[] }) {
  if (roleValues.length === 0) {
    return <span className="text-muted-foreground">No Helm role</span>
  }

  return (
    <div className="flex flex-wrap gap-1">
      {roleValues.map((role) => (
        <Badge key={role} variant={role === 'admin' ? 'default' : 'secondary'}>
          {formatRole(role)}
        </Badge>
      ))}
    </div>
  )
}

function UserRoleEditor({
  user,
  currentUserId,
}: {
  user: HelmUser
  currentUserId: string
}) {
  const router = useRouter()
  const initialRoles = useMemo(() => getRoleValues(user), [user])
  const [selectedRoles, setSelectedRoles] = useState<HelmUserRole[]>(initialRoles)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasChanged = selectedRoles.slice().sort().join(',') !== initialRoles.slice().sort().join(',')
  const removesOwnAdmin = user.id === currentUserId && !selectedRoles.includes('admin')

  function toggleRole(role: HelmUserRole, checked: boolean) {
    setSelectedRoles((current) => {
      if (checked) {
        return current.includes(role) ? current : [...current, role]
      }

      return current.filter((item) => item !== role)
    })
  }

  async function saveRoles() {
    setIsSaving(true)
    setError(null)

    try {
      await apiClient.updateHelmUserRoles(user.id, { roles: selectedRoles })
      router.refresh()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update roles')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {roles.map((role) => (
          <label key={role.value} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
            <Checkbox
              checked={selectedRoles.includes(role.value)}
              onCheckedChange={(checked) => toggleRole(role.value, checked === true)}
            />
            <span>{role.label}</span>
          </label>
        ))}
      </div>

      {removesOwnAdmin && (
        <p className="text-sm text-destructive">You cannot remove your own admin role.</p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex gap-2">
        <Button size="sm" onClick={saveRoles} disabled={!hasChanged || isSaving || removesOwnAdmin}>
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!hasChanged || isSaving}
          onClick={() => setSelectedRoles(initialRoles)}
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      </div>
    </div>
  )
}

function InviteStaffForm() {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<HelmUserRole>('viewer')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdInvitation, setCreatedInvitation] = useState<HelmStaffInvitation | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setIsSubmitting(true)
    setError(null)
    setCreatedInvitation(null)

    try {
      const invitation = await apiClient.inviteHelmUser({ email: email.trim(), role })
      setCreatedInvitation(invitation)
      setEmail('')
      setRole('viewer')
      router.refresh()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to invite user')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function copyInviteUrl() {
    if (!createdInvitation?.invitation_url) return
    await navigator.clipboard.writeText(createdInvitation.invitation_url)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite User</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 md:grid-cols-[1fr_220px_auto]">
            <div className="space-y-2">
              <Label htmlFor="invite_email">Email</Label>
              <Input
                id="invite_email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="teammate@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite_role">Role</Label>
              <Select value={role} onValueChange={(value) => setRole(value as HelmUserRole)}>
                <SelectTrigger id="invite_role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Invite
              </Button>
            </div>
          </div>

          {createdInvitation?.invitation_url && (
            <div className="space-y-2">
              <Label htmlFor="invitation_url">Invitation Link</Label>
              <div className="flex gap-2">
                <Input id="invitation_url" value={createdInvitation.invitation_url} readOnly />
                <Button type="button" variant="outline" onClick={copyInviteUrl}>
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </div>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  )
}

function InvitationStatusBadge({ invitation }: { invitation: HelmStaffInvitation }) {
  if (invitation.status === 'ACCEPTED') {
    return <Badge variant="secondary">Accepted</Badge>
  }

  if (invitation.status === 'REJECTED') {
    return <Badge variant="outline">Revoked</Badge>
  }

  return <Badge>Pending</Badge>
}

function PendingInvitations({ invitations }: { invitations: HelmStaffInvitation[] }) {
  const router = useRouter()
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function revokeInvitation(id: string) {
    setRevokingId(id)
    setError(null)

    try {
      await apiClient.revokeHelmInvitation(id)
      router.refresh()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to revoke invitation')
    } finally {
      setRevokingId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invitations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {invitations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No invitations yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell className="font-medium">{invitation.invited_email}</TableCell>
                  <TableCell>{formatRole(invitation.role)}</TableCell>
                  <TableCell><InvitationStatusBadge invitation={invitation} /></TableCell>
                  <TableCell>{formatDate(invitation.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {invitation.status === 'PENDING' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => revokeInvitation(invitation.id)}
                        disabled={revokingId === invitation.id}
                      >
                        {revokingId === invitation.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                        Revoke
                      </Button>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

export function StaffUserManagement({
  users,
  invitations,
  currentUserId,
}: {
  users: HelmUser[]
  invitations: HelmStaffInvitation[]
  currentUserId: string
}) {
  return (
    <div className="space-y-6">
      <InviteStaffForm />

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Current Roles</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="w-[420px]">Manage Roles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="font-medium">{user.display_name || user.name || user.email}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </TableCell>
                    <TableCell><RoleBadges roles={getRoleValues(user)} /></TableCell>
                    <TableCell>{formatDate(user.last_seen_at)}</TableCell>
                    <TableCell><UserRoleEditor user={user} currentUserId={currentUserId} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PendingInvitations invitations={invitations} />
    </div>
  )
}
